import React, { useEffect, useState } from "react";
import {
  getMyRoles,
  setEmulatedRole,
  clearEmulatedRole,
  getEmulatedRole,
  checkIfEmulating,
  initializeEmulationState,
  getEmulateableRoles,
  roleDescriptions,
} from "../../services/userService";
import { preferencesService } from "../../services/preferencesService";
import { MdEmail, MdTextsms, MdPhone } from "react-icons/md";

const NotificationPreference = ({ icon: Icon, label, checked, onChange }) => (
  <div className="flex items-center justify-between py-2">
    <div className="flex items-center">
      <Icon className="text-xl mr-3 text-blue-400" />
      <span className="text-base">{label}</span>
    </div>
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        onChange={onChange}
      />
      <div className="w-9 h-5 bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-500"></div>
    </label>
  </div>
);

const UserProfile = ({ profile }) => {
  const [roles, setRoles] = useState([]);
  const [emulatedRole, setEmulatedRoleState] = useState(null);
  const [isEmulating, setIsEmulating] = useState(false);
  const [emulateableRoles, setEmulateableRoles] = useState([]);
  const [preferences, setPreferences] = useState({
    email: false,
    text: false,
    phone_number: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      initializeEmulationState();
      const fetchedRoles = await getMyRoles();
      const lowerCaseRoles = fetchedRoles.map((role) => role.toLowerCase());
      setRoles(lowerCaseRoles);
      setEmulatedRoleState(getEmulatedRole());
      setIsEmulating(checkIfEmulating());
      setEmulateableRoles(getEmulateableRoles(lowerCaseRoles).reverse());
      try {
        const userPreferences = await preferencesService.getUserPreferences();
        setPreferences(userPreferences);
      } catch (error) {
        console.error("Error fetching user preferences:", error);
      }
    };
    fetchData();
  }, []);

  const updatePreferences = async (newPreferences) => {
    setPreferences(newPreferences);
    try {
      await preferencesService.updateUserPreferences(newPreferences);
    } catch (error) {
      console.error("Error updating user preferences:", error);
    }
  };

  const handleEmulateRole = async (role) => {
    if (setEmulatedRole(role, roles)) {
      setEmulatedRoleState(role);
      setIsEmulating(true);
      const fetchedRoles = await getMyRoles();
      setRoles(fetchedRoles.map((r) => r.toLowerCase()));
      window.dispatchEvent(new Event('refreshUserIcon'));
    }
  };

  const handleClearEmulation = async () => {
    clearEmulatedRole();
    setEmulatedRoleState(null);
    setIsEmulating(false);
    const fetchedRoles = await getMyRoles();
    setRoles(fetchedRoles.map((r) => r.toLowerCase()));
    window.dispatchEvent(new Event('refreshUserIcon'));
  };

  const handleEmailNotificationChange = (checked) => {
    updatePreferences({ ...preferences, email: checked });
  };

  const handleTextNotificationChange = (checked) => {
    updatePreferences({ ...preferences, text: checked, phone_number: checked ? preferences.phone_number : null });
  };

  const handlePhoneNumberChange = (e) => {
    updatePreferences({ ...preferences, phone_number: e.target.value });
  };

  const formatRoleName = (role) => {
    return role
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (!profile) {
    return <div className="text-white">Loading profile ...</div>;
  }

  return (
    <div className="bg-gray-900 text-white p-8 rounded-xl max-w-2xl mx-auto">
      <h2 className="text-3xl font-bold mb-8">User Profile</h2>
      <div className="mb-8 space-y-4">
        <div className="flex">
          <span className="font-semibold w-24 flex-shrink-0">Name</span>
          <span className="font-normal">{profile.name}</span>
        </div>
        <div className="flex">
          <span className="font-semibold w-24 flex-shrink-0">Email</span>
          <span className="font-normal">{profile.email}</span>
        </div>
      </div>
      
      <div className="mb-6 bg-gray-800 p-4 rounded-xl">
        <h3 className="text-xl font-semibold mb-4">Notification Preferences</h3>
        <div className="space-y-2">
          <NotificationPreference
            icon={MdEmail}
            label="Email"
            checked={preferences.email}
            onChange={(e) => handleEmailNotificationChange(e.target.checked)}
          />
          <NotificationPreference
            icon={MdTextsms}
            label="Text"
            checked={preferences.text}
            onChange={(e) => handleTextNotificationChange(e.target.checked)}
          />
        </div>
        {preferences.text && (
          <div className="mt-3 flex items-center">
            <MdPhone className="text-xl mr-3 text-blue-400" />
            <input
              type="tel"
              id="phoneNumber"
              value={preferences.phone_number || ''}
              onChange={handlePhoneNumberChange}
              className="flex-grow bg-gray-700 text-white px-3 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="+1 (333) 222-4444"
            />
          </div>
        )}
      </div>

      <h3 className="text-2xl font-semibold mb-6">Roles</h3>
      <ul className="space-y-4">
        {Object.entries(roleDescriptions).map(([role, details]) => (
          <li
            key={role}
            className={`p-4 rounded-lg border ${
              roles.includes(role)
                ? "bg-blue-800 bg-opacity-50 border-blue-400"
                : "bg-gray-800 bg-opacity-50 border-gray-600"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <p
                className={`font-bold text-lg ${
                  roles.includes(role) ? "text-blue-200" : "text-gray-200"
                }`}
              >
                {formatRoleName(role)}
              </p>
              <div className="flex items-center justify-end mb-2">
                {roles.includes(role) &&
                  isEmulating &&
                  emulatedRole === role && (
                    <span className="bg-red-500 bg-opacity-90 text-white text-xs font-semibold px-2 py-1 me-2 rounded-full">
                      Emulated
                    </span>
                  )}
                {roles.includes(role) && (
                  <span className="bg-blue-600 text-white text-xs font-semibold px-2 py-1 rounded-full">
                    Assigned
                  </span>
                )}
              </div>
            </div>
            <p className="text-sm text-gray-300 mb-1">{details.description}</p>
            <p className="text-sm text-gray-400">
              <span className="font-semibold">Permissions:</span>{" "}
              {details.permissions}
            </p>
          </li>
        ))}
      </ul>
      <div className="mt-8">
        {isEmulating ? (
          <div>
            <p className="italic mb-2">Emulation in use</p>
            <button
              onClick={handleClearEmulation}
              className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded transition duration-300"
            >
              Stop Emulating
            </button>
          </div>
        ) : (
          <div>
            {emulateableRoles.length > 0 && (
              <p className="italic mb-2">Select a role to emulate</p>
            )}
            <div className="flex flex-wrap gap-2">
              {emulateableRoles.map((role) => (
                <button
                  key={role}
                  onClick={() => handleEmulateRole(role)}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded transition duration-300"
                >
                  {formatRoleName(role)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;